import { useMemo, useState, type ReactNode } from 'react';
import { Check, ChevronRight, Search } from 'lucide-react';
import {
  STATUS_GROUP_LABEL,
  STATUS_GROUP_ORDER,
  contrastRatio,
  parseHexColor,
  readablePillBackground,
  type Status,
  type StatusType,
} from '@karya/core';
import { cn } from '@/lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/overlay';

/** Outlined circle (not started), half-filled (active), filled with check (done/closed). */
export function StatusIcon({
  status,
  size = 16,
  className,
}: {
  status?: Pick<Status, 'color' | 'type'> | null;
  size?: number;
  className?: string;
}) {
  const color = status?.color ?? 'var(--status-todo)';
  const type: StatusType = status?.type ?? 'not_started';
  if (type === 'done' || type === 'closed') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        className={cn('shrink-0', className)}
        aria-hidden="true"
      >
        <circle cx="8" cy="8" r="7.25" fill={color} />
        <path
          d="M4.8 8.2 7 10.3l4.2-4.6"
          fill="none"
          stroke="#fff"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      className={cn('shrink-0', className)}
      aria-hidden="true"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeDasharray={type === 'not_started' ? '2.6 1.9' : undefined}
      />
      {type === 'active' && <path d="M8 3.6a4.4 4.4 0 0 1 0 8.8z" fill={color} />}
    </svg>
  );
}

/** White text on dark status colours, ink on light ones. */
/** White text when it is readable (darkening the colour a little if needed); ink on light colours. */
function pillColors(color: string): { background: string; color: string } {
  const c = parseHexColor(color);
  if (c && contrastRatio(c, [255, 255, 255]) < 4.5 && contrastRatio(c, [27, 37, 38]) >= 7)
    return { background: color, color: '#1b2526' };
  return { background: readablePillBackground(color), color: '#ffffff' };
}

export function StatusPill({
  status,
  className,
  children,
}: {
  status?: Pick<Status, 'color' | 'name' | 'type'> | null;
  className?: string;
  children?: ReactNode;
}) {
  const color = status?.color ?? '#87909e';
  return (
    <span
      className={cn(
        'label-caps inline-flex h-5 items-center gap-1.5 rounded-xs px-2 whitespace-nowrap',
        className,
      )}
      style={pillColors(color)}
    >
      {(status?.type === 'closed' || status?.type === 'done') && <Check className="size-3" />}
      {status?.name ?? 'No status'}
      {children}
    </span>
  );
}

/** Searchable status picker grouped like ClickUp: Not started / Active / Done / Closed. */
export function StatusMenu({
  statuses,
  value,
  onChange,
  children,
  align = 'start',
}: {
  statuses: Status[];
  value: string | null;
  onChange: (statusId: string) => void;
  children: ReactNode;
  align?: 'start' | 'end' | 'center';
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const groups = useMemo(() => {
    const f = q.trim().toLowerCase();
    return STATUS_GROUP_ORDER.map((type) => ({
      type,
      items: statuses.filter((s) => s.type === type && (!f || s.name.toLowerCase().includes(f))),
    })).filter((g) => g.items.length > 0);
  }, [statuses, q]);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQ('');
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-60 p-1" onClick={(e) => e.stopPropagation()}>
        <label className="flex h-8 items-center gap-2 rounded-sm bg-surface-2 px-2 text-sm text-ink-3">
          <Search className="size-3.5" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search statuses"
            className="min-w-0 flex-1 bg-transparent text-ink outline-none placeholder:text-ink-3"
            aria-label="Search statuses"
          />
        </label>
        <div
          role="listbox"
          aria-label="Statuses"
          className="mt-1 max-h-72 overflow-y-auto scroll-thin"
        >
          {groups.map((g) => (
            <div key={g.type}>
              <div className="px-2 pt-2 pb-1 text-cap text-ink-3">{STATUS_GROUP_LABEL[g.type]}</div>
              {g.items.map((s) => (
                <button
                  key={s.id}
                  role="option"
                  aria-selected={s.id === value}
                  onClick={() => {
                    onChange(s.id);
                    setOpen(false);
                  }}
                  className="flex h-7 w-full items-center gap-2 rounded-sm px-2 text-left hover:bg-surface-2"
                >
                  <StatusIcon status={s} />
                  <span className="label-caps flex-1 truncate text-ink">{s.name}</span>
                  {s.id === value && <Check className="size-4 text-primary" />}
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Status pill + next-status arrow + complete button, as in the task detail header. */
export function StatusControl({
  statuses,
  value,
  onChange,
  onComplete,
  nextStatus,
}: {
  statuses: Status[];
  value: Status | undefined;
  onChange: (id: string) => void;
  onComplete: () => void;
  nextStatus: Status | null;
}) {
  const complete = value?.type === 'closed' || value?.type === 'done';
  return (
    <div className="flex items-center gap-1">
      <StatusMenu statuses={statuses} value={value?.id ?? null} onChange={onChange}>
        <button
          className="rounded-xs"
          aria-label={`Status: ${value?.name ?? 'none'}. Change status`}
        >
          <StatusPill status={value} />
        </button>
      </StatusMenu>
      {nextStatus && !complete && (
        <button
          onClick={() => onChange(nextStatus.id)}
          title={`Move to ${nextStatus.name}`}
          aria-label={`Move to ${nextStatus.name}`}
          className="inline-flex size-5 items-center justify-center rounded-xs border border-line text-ink-2 hover:bg-surface-2"
        >
          <ChevronRight className="size-3.5" />
        </button>
      )}
      <button
        onClick={onComplete}
        title={complete ? 'Reopen' : 'Complete'}
        aria-label={complete ? 'Reopen task' : 'Complete task'}
        className={cn(
          'inline-flex size-5 items-center justify-center rounded-xs border',
          complete
            ? 'border-done bg-done text-white'
            : 'border-line text-ink-2 hover:border-done hover:text-done',
        )}
      >
        <Check className="size-3.5" />
      </button>
    </div>
  );
}
